import { normalizeRole, USER_ROLES } from '../roles.js'
import { roleHomePath } from '../roleHomePath.js'

export function getRoleHomePath(roleOrProfile) {
  if (roleOrProfile && typeof roleOrProfile === 'object') {
    return roleHomePath(roleOrProfile)
  }
  return roleHomePath({ role: roleOrProfile, role_canonical: roleOrProfile })
}

/**
 * Role dashboard paths where Layer A orientation may auto-open.
 * @param {string} pathname
 * @param {string | undefined} role
 */
export function isDashboardPath(pathname, role) {
  if (role === 'client') return pathname === '/client' || pathname === '/client/'
  if (role === 'admin' || role === 'account_manager') {
    return pathname === '/admin' || pathname === '/admin/'
  }
  if (role === 'influencer') return pathname === '/influencer' || pathname === '/influencer/'
  return false
}

/**
 * Paths that must not trigger orientation redirect to role home.
 * @param {string} pathname
 */
export function isOrientationRedirectSkippedPath(pathname) {
  return (
    pathname.startsWith('/login')
    || pathname.startsWith('/join')
    || pathname.startsWith('/complete-onboarding')
    || pathname.startsWith('/influencer/onboarding')
    || pathname.startsWith('/super-admin')
  )
}

/**
 * Whether an incomplete orientation should redirect the user to their role home.
 * @param {{ pathname: string, role?: string, shouldShowOrientation: boolean, isDashboard: boolean }} params
 */
export function shouldRedirectForPendingOrientation({
  pathname,
  role,
  shouldShowOrientation,
  isDashboard,
}) {
  if (!shouldShowOrientation || !role) return false
  if (!getRoleHomePath(role)) return false
  if (isDashboard) return false
  if (isOrientationRedirectSkippedPath(pathname)) return false
  return true
}

/** @typedef {'account_manager' | 'client' | 'influencer'} OnboardingRole */

/**
 * @param {string | undefined} role
 * @param {string | null | undefined} tenantId
 * @returns {OnboardingRole | null}
 */
export function getOnboardingRole(role, tenantId = undefined) {
  const canonical = normalizeRole(role, tenantId)
  if (canonical === USER_ROLES.ACCOUNT_MANAGER) return 'account_manager'
  if (canonical === USER_ROLES.CLIENT) return 'client'
  if (canonical === USER_ROLES.INFLUENCER) return 'influencer'
  return null
}

/** @type {{ id: string, roles: OnboardingRole[], label: string }[]} */
export const SURFACE_REGISTRY = [
  { id: 'admin.clients', roles: ['account_manager'], label: 'Clients' },
  { id: 'admin.campaigns', roles: ['account_manager'], label: 'Campaigns' },
  { id: 'campaign.casting', roles: ['account_manager', 'client'], label: 'Casting' },
  { id: 'campaign.pipeline', roles: ['account_manager'], label: 'Creator pipeline' },
  { id: 'campaign.content_review', roles: ['account_manager', 'client'], label: 'Content review' },
  { id: 'client.approvals', roles: ['client'], label: 'Approvals' },
  { id: 'client.campaigns', roles: ['client'], label: 'My Campaigns' },
  { id: 'influencer.tasks', roles: ['influencer'], label: 'My Tasks' },
  { id: 'influencer.campaigns', roles: ['influencer'], label: 'My Campaigns' },
  { id: 'influencer.post_metrics', roles: ['influencer'], label: 'Post metrics' },
  { id: 'influencer.submissions', roles: ['influencer'], label: 'My Submissions' },
]

/**
 * @param {OnboardingRole | null} onboardingRole
 */
export function getSurfacesForRole(onboardingRole) {
  if (!onboardingRole) return []
  return SURFACE_REGISTRY.filter((s) => s.roles.includes(onboardingRole))
}

/**
 * @param {string} surfaceId
 * @param {string | undefined} role
 * @param {string | null | undefined} tenantId
 */
export function isSurfaceAvailableForUser(surfaceId, role, tenantId = undefined) {
  const onboardingRole = getOnboardingRole(role, tenantId)
  if (!onboardingRole) return false
  const entry = SURFACE_REGISTRY.find((s) => s.id === surfaceId)
  return Boolean(entry?.roles.includes(onboardingRole))
}

/**
 * Campaign detail hash → surface guide id.
 * @param {string | undefined} role
 * @param {string} [hash]
 * @param {string | null | undefined} [tenantId]
 */
export function getCampaignGuideSurfaceId(role, hash = '', tenantId = undefined) {
  const h = (hash || '').replace(/^#/, '').toLowerCase()
  const onboardingRole = getOnboardingRole(role, tenantId)
  if (onboardingRole === 'account_manager') {
    if (h === 'content-board') return 'campaign.content_review'
    if (h === 'influencers-board') return 'campaign.pipeline'
    return 'campaign.casting'
  }
  if (onboardingRole === 'client') {
    if (h === 'content-board') return 'campaign.content_review'
    return 'campaign.casting'
  }
  return null
}
